import React from 'react';
import { Person } from 'talent_matching/types';
import { withLocalePrefix } from 'shared/utils/translation';
import { useModal } from '@grnhse/seedling/lib/birch/components/modals';
import { useUserContext } from 'talent_matching/contexts/user_context';
import styled from 'styled-components';
import FeedbackModal from 'talent_matching/components/modals/leave_feedback_modal';
import { standardRequestWithFlash } from 'shared/utils/request';
import RejectModal from 'candidate_profile_redesign/modals/reject/reject_modal';
import useQueryHelper from 'talent_matching/hooks/use_query_helper';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import {
  EllipsisMenuButton,
  ExpandableMenu,
  MenuItem,
  MenuList,
} from '@grnhse/seedling/lib/birch/components/expandable_menu';
import { Tooltip } from '@grnhse/seedling/lib/birch/components/tooltips';

const t = withLocalePrefix('talent_matching');

type Props = {
  candidate: Person;
  onRejectSuccess: (candidate: Person, closeModal: () => void) => void;
};

const Wrapper = styled.div`
  padding-top: ${Tokens.spacing['1']};
  height: 100%;
`;

export const CandidateRowActions = ({ candidate, onRejectSuccess }: Props) => {
  const { excludePeople } = useQueryHelper();
  const { paths, canAdvance, feedbackOnly, canReject, appReviewSessionId } =
    useUserContext();

  const [showFeedbackModal, openFeedbackModal, closeFeedbackModal] = useModal();
  const [rejectModalOpen, openRejectModal, closeRejectModal] = useModal();

  const handleWrapperClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleAdvanceCandidate = async () => {
    if (!candidate.advancePath) {
      return;
    }

    try {
      await standardRequestWithFlash.post(candidate.advancePath, {
        app_review_session_id: appReviewSessionId,
        source: 'talent_matching',
        action_source: 'table_row',
      });
      excludePeople([candidate.id]);
    } catch {
      return;
    }
  };

  return (
    <Wrapper onClick={handleWrapperClick} onMouseUp={handleWrapperClick}>
      {/* @ts-expect-error - TS2322 */}
      <ExpandableMenu dangerouslyBypassScrollLock>
        <EllipsisMenuButton aria-label={t('table.labels.actions')} />
        <MenuList>
          <MenuItem onSelect={openFeedbackModal}>
            {t('table.labels.feedback')}
          </MenuItem>
          {!feedbackOnly && (
            <>
              {canReject ? (
                <MenuItem onSelect={openRejectModal}>{t('reject')}</MenuItem>
              ) : (
                <Tooltip
                  label={t('table.tooltips.cannot_reject')}
                  ariaLabel={t('table.tooltips.cannot_reject')}
                >
                  <MenuItem onSelect={openRejectModal} disabled={true}>
                    {t('reject')}
                  </MenuItem>
                </Tooltip>
              )}
              {canAdvance ? (
                <MenuItem onSelect={handleAdvanceCandidate}>
                  {t('table.labels.advance')}
                </MenuItem>
              ) : (
                <Tooltip
                  label={t('table.tooltips.cannot_advance')}
                  ariaLabel={t('table.tooltips.cannot_advance')}
                >
                  <MenuItem onSelect={handleAdvanceCandidate} disabled={true}>
                    {t('table.labels.advance')}
                  </MenuItem>
                </Tooltip>
              )}
            </>
          )}
        </MenuList>
      </ExpandableMenu>

      <FeedbackModal
        isOpen={showFeedbackModal}
        closeModal={closeFeedbackModal}
        application={candidate}
        hiringPlanTagsPath={paths.hiringPlanTagsPath}
      />

      <RejectModal
        bulkMode={false}
        closeRejectModal={closeRejectModal}
        isRejectModalOpen={rejectModalOpen}
        getRejectModalPath={candidate.rejectModalPath}
        candidateName={candidate.fullName}
        changeApplicationStatePath={candidate.rejectPath}
        rejectionTrackingPayload={{
          app_review_session_id: appReviewSessionId,
          source: 'talent_matching',
          action_source: 'table_row',
        }}
        onRejectSuccess={() => {
          onRejectSuccess(candidate, closeRejectModal);
        }}
      />
    </Wrapper>
  );
};

export default CandidateRowActions;
